import React, { useEffect, useMemo, useState } from "react";
import { Spin, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useGetGoodsInventoryQuery } from "../../../utils/redux/api/financeApi";
import { InventoryModal } from "./InventoryModal";

interface RevenueType { _id: string; name: string; }
interface Location { _id: string; name: string; }

interface InventorySectionProps {
  clientId: string;
  selectedMonths: string[];
  revenueTypes: RevenueType[];
  availableLocations: Location[];
  locationFilters: string[];
  onStockChange?: (totalStock: number) => void;
  canManage?: boolean;
}

const fmt = (n: number) => Math.round(n).toLocaleString("sr-RS");

// Split "Venue Name - City" into two parts for the header
const splitLocName = (name: string) => {
  const idx = name.lastIndexOf(" - ");
  if (idx === -1) return { main: name, city: null };
  return { main: name.substring(0, idx).trim(), city: name.substring(idx + 3).trim() };
};

export const InventorySection: React.FC<InventorySectionProps> = ({
  clientId, selectedMonths, revenueTypes,
  availableLocations, locationFilters, onStockChange, canManage = false,
}) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);

  const activeMonth = useMemo(() => {
    const sorted = [...selectedMonths].sort();
    return sorted[sorted.length - 1] ?? dayjs().format("YYYY-MM");
  }, [selectedMonths]);

  const currentMonth = useMemo(() => {
    const d = dayjs(activeMonth, "YYYY-MM");
    return { month: d.month() + 1, year: d.year() };
  }, [activeMonth]);

  const defaultModalLocationId = useMemo(() => {
    if (locationFilters.length === 1) return locationFilters[0];
    return availableLocations[0]?._id;
  }, [locationFilters, availableLocations]);

  const monthsKey = selectedMonths.join(",");

  const { data: inventoryData, isLoading: loading, refetch: fetchInventory } =
    useGetGoodsInventoryQuery(
      { clientId, months: monthsKey },
      { skip: !clientId || selectedMonths.length === 0 },
    );
  const inventoryEntries = inventoryData?.data || [];

  const locationsToShow = useMemo(() => {
    if (locationFilters.length === 0) return availableLocations;
    return availableLocations.filter((l) => locationFilters.includes(l._id));
  }, [availableLocations, locationFilters]);

  const multiLoc = locationsToShow.length > 1;

  // Per revenue type: closingStock per location
  const rows = useMemo(() => {
    return revenueTypes.map((rt) => {
      const locationValues = locationsToShow.map((loc) => {
        const entry = inventoryEntries.find(
          (e) => e.revenueTypeId === rt._id && e.locationId === loc._id &&
            e.month === currentMonth.month && e.year === currentMonth.year,
        );
        return { loc, value: entry?.closingStock ?? 0 };
      });
      const total = locationValues.reduce((s, lv) => s + lv.value, 0);
      return { rt, locationValues, total };
    });
  }, [revenueTypes, inventoryEntries, locationsToShow, currentMonth]);

  const totalStock = useMemo(() => rows.reduce((s, r) => s + r.total, 0), [rows]);

  // Column totals per location (for footer)
  const colTotals = useMemo(() =>
    locationsToShow.map((loc) =>
      rows.reduce((s, r) => s + (r.locationValues.find((lv) => lv.loc._id === loc._id)?.value ?? 0), 0),
    ), [locationsToShow, rows]);

  useEffect(() => { onStockChange?.(totalStock); }, [totalStock, onStockChange]);

  const hasAnyEntry = inventoryEntries.some((e) =>
    e.month === currentMonth.month && e.year === currentMonth.year,
  );

  const periodLabel = useMemo(() => {
    const sorted = [...selectedMonths].sort();
    if (sorted.length === 1) return dayjs(sorted[0], "YYYY-MM").format("MMM YYYY");
    return `${dayjs(sorted[0], "YYYY-MM").format("MMM YYYY")} – ${dayjs(sorted[sorted.length - 1], "YYYY-MM").format("MMM YYYY")}`;
  }, [selectedMonths]);

  const LOC_COL_W = 120;
  const TOTAL_COL_W = 100;
  const NAME_COL_W = 190;

  const colTemplate = multiLoc
    ? `${NAME_COL_W}px ${locationsToShow.map(() => `${LOC_COL_W}px`).join(" ")} ${TOTAL_COL_W}px`
    : "1fr 130px";

  const tableMinWidth = multiLoc
    ? NAME_COL_W + locationsToShow.length * LOC_COL_W + TOTAL_COL_W
    : undefined;

  return (
    <section className="fin-overview fin-inventory">
      <div className="fo-head">
        <div>
          <h2>{t("Goods Inventory")}</h2>
          <div className="fo-sub">{t("Track goods on stock and compute actual consumption")}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="fo-period-tag">{periodLabel}</span>
          {canManage && (
            <button className="fin-btn" onClick={() => setModalVisible(true)} style={{ whiteSpace: "nowrap" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {t("Enter Goods Popis")}
            </button>
          )}
        </div>
      </div>

      <Spin spinning={loading}>
        {revenueTypes.length === 0 ? (
          <div style={{ color: "#9ca3af", fontSize: 13, padding: "16px 0" }}>
            {t("No revenue types configured. Add them in Settings → Daily Revenue.")}
          </div>
        ) : (
          <>
            <div className="fin-inv-scroll-wrap">
              <div className="fin-inv-table" style={tableMinWidth ? { minWidth: tableMinWidth } : undefined}>
                {/* Header */}
                <div className="fin-inv-trow fin-inv-thead" style={{ gridTemplateColumns: colTemplate }}>
                  <span />
                  {multiLoc
                    ? locationsToShow.map((loc) => {
                        const { main, city } = splitLocName(loc.name);
                        return (
                          <Tooltip key={loc._id} title={loc.name} placement="top">
                            <span className="fin-inv-loc-header">
                              <span className="fin-inv-loc-main">{main}</span>
                              {city && <span className="fin-inv-loc-city">{city}</span>}
                            </span>
                          </Tooltip>
                        );
                      })
                    : null}
                  <span className="fin-inv-loc-header fin-inv-loc-total-header">
                    {multiLoc ? t("Total") : t("Value")}
                  </span>
                </div>

                {/* Revenue type rows */}
                {rows.map(({ rt, locationValues, total }) => (
                  <div key={rt._id} className="fin-inv-trow" style={{ gridTemplateColumns: colTemplate }}>
                    <span className="fin-inv-tcell-name">{rt.name}</span>
                    {multiLoc && locationValues.map(({ loc, value }) => (
                      <span key={loc._id} className="fin-inv-tcell">{fmt(value)}</span>
                    ))}
                    <span className={`fin-inv-tcell${multiLoc ? " fin-inv-tcell--total" : ""}`}>{fmt(total)}</span>
                  </div>
                ))}

                {/* Footer */}
                <div className="fin-inv-trow fin-inv-trow--footer" style={{ gridTemplateColumns: colTemplate }}>
                  <span className="fin-inv-footer-label">{t("Total")}</span>
                  {multiLoc && colTotals.map((ct, i) => (
                    <span key={i} className="fin-inv-tcell">{fmt(ct)}</span>
                  ))}
                  <span className="fin-inv-tcell fin-inv-tcell--total">{fmt(totalStock)}</span>
                </div>
              </div>
            </div>

            {/* KPI */}
            <div className="fin-inv-kpis">
              <div className="fin-inv-kpi">
                <span>{t("Total goods value (closing stock)")}</span>
                <strong>{fmt(totalStock)} <em>RSD</em></strong>
              </div>
            </div>

            {!hasAnyEntry && (
              <div className="fin-inv-empty-hint">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <circle cx="12" cy="12" r="9" /><path d="M12 8v4m0 4h.01" />
                </svg>
                {t("No inventory entered yet for this period. Click Enter Goods Popis to add.")}
              </div>
            )}
          </>
        )}
      </Spin>

      <InventoryModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSaved={fetchInventory}
        clientId={clientId}
        month={activeMonth}
        revenueTypes={revenueTypes}
        availableLocations={availableLocations}
        defaultLocationId={defaultModalLocationId}
        existingEntries={inventoryEntries}
      />
    </section>
  );
};
